import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Columns3,
  LayoutGrid,
  List,
  Plus,
  Target,
  Clock,
} from 'lucide-react';
import { Skeleton } from '../../../shared/components/Skeleton';
import { Alert } from '../../../shared/components/Alert';
import { EmptyState } from '../../../shared/components/EmptyState';
import { useOportunidades } from '../hooks';
import type { OportunidadeListItem } from '../api';
import { formatCurrencyBRL } from '../utils/format';

const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

type EventKind = 'closing' | 'action';

interface CalendarEvent {
  kind: EventKind;
  card: OportunidadeListItem;
}

/**
 * Visão Calendário (US-COM-004 §Filtros e Visões, task 14.3). Grid
 * mensal custom em CSS Grid — sem lib de calendário para manter bundle
 * enxuto. Renderiza dois tipos de eventos:
 *
 *   - `closing` (azul): `expected_closing_date` — data prevista de
 *     fechamento
 *   - `action` (âmbar): `next_action_date` — próxima ação agendada
 *
 * Uma mesma oportunidade pode aparecer em duas datas se ambos os
 * campos estão preenchidos. Clique num evento vai para o detalhe.
 * Navegação por mês: anterior / próximo / "Hoje". Só oportunidades
 * abertas entram — desfechos e rascunhos não poluem a visão.
 */
export default function OportunidadesCalendarioPage(): React.ReactElement {
  const navigate = useNavigate();
  const [monthStart, setMonthStart] = useState(startOfMonth(new Date()));

  const { from, to } = useMemo(() => monthWindow(monthStart), [monthStart]);

  // Buscamos todas as oportunidades abertas e filtramos client-side
  // pelo mês visível. Estratégia MVP — o dataset aberto costuma ser
  // pequeno (<1000 registros no piloto). Se o pipeline crescer,
  // migramos para 2 queries paralelas (uma por expectedClosing, outra
  // por nextAction) ou expomos um filtro OR no backend.
  const { data, isLoading, isError, error } = useOportunidades({
    status: 'aberta',
    limit: 500,
  });

  // Eventos por dia (chave = YYYY-MM-DD)
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    const push = (day: string, evt: CalendarEvent) => {
      const arr = map.get(day) ?? [];
      arr.push(evt);
      map.set(day, arr);
    };
    for (const o of data?.data ?? []) {
      if (o.expectedClosingDate) {
        const d = o.expectedClosingDate.slice(0, 10);
        if (inRange(d, from, to)) push(d, { kind: 'closing', card: o });
      }
      if (o.nextActionDate) {
        const d = o.nextActionDate.slice(0, 10);
        if (inRange(d, from, to)) push(d, { kind: 'action', card: o });
      }
    }
    return map;
  }, [data, from, to]);

  const cells = useMemo(() => buildMonthCells(monthStart), [monthStart]);

  const today = new Date().toISOString().slice(0, 10);
  const monthLabel = `${MONTHS[monthStart.getMonth()]} de ${monthStart.getFullYear()}`;

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Oportunidades — visão Calendário
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Fechamentos previstos e próximas ações agendadas.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => navigate('/oportunidades')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <List className="h-4 w-4" />
            Lista
          </button>
          <button
            onClick={() => navigate('/oportunidades/cards')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <LayoutGrid className="h-4 w-4" />
            Cards
          </button>
          <button
            onClick={() => navigate('/oportunidades/kanban')}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
          >
            <Columns3 className="h-4 w-4" />
            Kanban
          </button>
          <button
            onClick={() => navigate('/oportunidades/nova')}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            Nova oportunidade
          </button>
        </div>
      </div>

      {/* Navegação de mês + legenda */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex items-center gap-1">
          <button
            onClick={() => setMonthStart(shiftMonth(monthStart, -1))}
            className="rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50"
            aria-label="Mês anterior"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <button
            onClick={() => setMonthStart(startOfMonth(new Date()))}
            className="rounded-md border border-gray-300 bg-white px-3 py-1 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Hoje
          </button>
          <button
            onClick={() => setMonthStart(shiftMonth(monthStart, 1))}
            className="rounded-md border border-gray-300 bg-white p-1.5 text-gray-700 hover:bg-gray-50"
            aria-label="Próximo mês"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
          <h2 className="ml-3 text-lg font-semibold text-gray-900">{monthLabel}</h2>
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-600">
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-blue-500" />
            Fechamento previsto
          </span>
          <span className="inline-flex items-center gap-1">
            <span className="h-2 w-2 rounded-full bg-amber-500" />
            Próxima ação
          </span>
        </div>
      </div>

      {isError && (
        <Alert variant="error" className="mb-4">
          Erro ao carregar eventos: {(error as Error)?.message ?? 'Falha desconhecida.'}
        </Alert>
      )}

      {isLoading ? (
        <Skeleton className="h-[500px] w-full" />
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
          {/* Cabeçalho de dias da semana */}
          <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-xs font-medium text-gray-600">
            {WEEKDAYS.map((w) => (
              <div key={w} className="p-2 text-center">
                {w}
              </div>
            ))}
          </div>
          {/* Grid do mês */}
          <div className="grid grid-cols-7">
            {cells.map((cell, i) => {
              const key = cell.iso;
              const events = eventsByDay.get(key) ?? [];
              const isToday = cell.iso === today;
              return (
                <div
                  key={i}
                  className={`min-h-[100px] border-b border-r border-gray-100 p-1.5 ${
                    cell.inMonth ? 'bg-white' : 'bg-gray-50 text-gray-400'
                  }`}
                >
                  <div className={`mb-1 text-xs ${isToday ? 'font-bold text-blue-700' : ''}`}>
                    {cell.day}
                  </div>
                  <div className="space-y-1">
                    {events.slice(0, 3).map((evt, idx) => (
                      <EventChip
                        key={`${evt.card.id}-${evt.kind}-${idx}`}
                        event={evt}
                        onClick={() => navigate(`/oportunidades/${evt.card.id}`)}
                      />
                    ))}
                    {events.length > 3 && (
                      <div className="text-[10px] text-gray-500">
                        +{events.length - 3} evento{events.length - 3 === 1 ? '' : 's'}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {!isLoading && (data?.data.length ?? 0) === 0 && (
        <div className="mt-4">
          <EmptyState
            title={`Nenhum evento em ${monthLabel}`}
            description="Ajuste a data ou verifique se as oportunidades abertas têm `expected_closing_date` ou `next_action_date` preenchidos."
          />
        </div>
      )}
    </div>
  );
}

const EventChip: React.FC<{ event: CalendarEvent; onClick: () => void }> = ({
  event,
  onClick,
}) => {
  const Icon = event.kind === 'closing' ? Target : Clock;
  const color =
    event.kind === 'closing'
      ? 'bg-blue-100 text-blue-800 hover:bg-blue-200'
      : 'bg-amber-100 text-amber-800 hover:bg-amber-200';
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex w-full items-center gap-1 truncate rounded px-1 py-0.5 text-left text-[10px] ${color}`}
      title={`${event.card.nome} — ${
        event.kind === 'closing' ? 'Fechamento previsto' : 'Próxima ação'
      } (${formatCurrencyBRL(event.card.valorEstimado)})`}
    >
      <Icon className="h-3 w-3 flex-shrink-0" />
      <span className="truncate">{event.card.codigo ?? event.card.nome}</span>
    </button>
  );
};

// ---------------------------------------------------------------------
// Helpers de data
// ---------------------------------------------------------------------

function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

function shiftMonth(d: Date, offset: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + offset, 1);
}

interface Cell {
  iso: string;
  day: number;
  inMonth: boolean;
}

/**
 * Gera 42 células (6 linhas × 7 colunas) para o mês, com dias do mês
 * anterior/próximo preenchendo as bordas.
 */
function buildMonthCells(monthStart: Date): Cell[] {
  const cells: Cell[] = [];
  const firstDay = monthStart.getDay(); // 0 (dom) – 6 (sáb)
  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0,
  ).getDate();
  const prev = new Date(monthStart);
  prev.setDate(0);
  const prevDays = prev.getDate();

  // Preenchimento inicial com dias do mês anterior
  for (let i = firstDay - 1; i >= 0; i--) {
    const d = new Date(
      monthStart.getFullYear(),
      monthStart.getMonth() - 1,
      prevDays - i,
    );
    cells.push({ iso: toIso(d), day: d.getDate(), inMonth: false });
  }
  // Dias do mês
  for (let d = 1; d <= daysInMonth; d++) {
    const dt = new Date(monthStart.getFullYear(), monthStart.getMonth(), d);
    cells.push({ iso: toIso(dt), day: d, inMonth: true });
  }
  // Complemento até 42 células
  const nextNeeded = 42 - cells.length;
  for (let d = 1; d <= nextNeeded; d++) {
    const dt = new Date(monthStart.getFullYear(), monthStart.getMonth() + 1, d);
    cells.push({ iso: toIso(dt), day: d, inMonth: false });
  }
  return cells;
}

function toIso(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function monthWindow(monthStart: Date): { from: string; to: string } {
  const daysInMonth = new Date(
    monthStart.getFullYear(),
    monthStart.getMonth() + 1,
    0,
  ).getDate();
  const end = new Date(monthStart.getFullYear(), monthStart.getMonth(), daysInMonth);
  return { from: toIso(monthStart), to: toIso(end) };
}

function inRange(day: string, from: string, to: string): boolean {
  return day >= from && day <= to;
}
